<template>
  <div :class="rootClass">
    <h6 v-if="!embedded" class="text-sm font-weight-bold mb-2">
      <i class="fab fa-jira text-primary me-1"></i>Jira
    </h6>
    <div v-if="loading" class="text-xs text-secondary py-2">
      <i class="fas fa-spinner fa-spin me-1"></i>Loading…
    </div>
    <div v-else-if="localLinks.length === 0" class="text-xs text-secondary py-2">
      No Jira links
    </div>
    <div v-else class="jira-panel-table-wrap">
      <table class="table table-sm mb-0 jira-panel-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Title</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="j in localLinks" :key="j.key">
            <td class="jira-panel-id">
              <a :href="j.url" target="_blank" rel="noopener" class="text-xs font-weight-bold">{{ j.key }}</a>
            </td>
            <td class="jira-panel-title text-xs text-secondary">{{ j.summary || "—" }}</td>
            <td class="jira-panel-status">
              <jira-status-select
                v-if="editable"
                :issue-key="j.key"
                :status="j.status || ''"
                :transitions="j.transitions || []"
                :compact="dense"
                :mini="dense"
                @update:status="j.status = $event"
                @update:transitions="j.transitions = $event"
              />
              <span v-else class="jira-status-readonly text-xs">{{ j.status || "—" }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="loadError" class="text-danger text-xs mt-2">{{ loadError }}</div>
  </div>
</template>

<script>
import JiraStatusSelect from "@/components/JiraStatusSelect.vue";
import { enrichJiraLinkRows, fetchJiraIssuesByKeys } from "@/services/jiraStatusApi";

function normalizeIncoming(links) {
  if (!Array.isArray(links)) return [];
  return links
    .map((l) => {
      if (!l) return null;
      if (typeof l === "string") {
        const key = l.trim().toUpperCase();
        return key ? { key, url: `#`, summary: "", status: "" } : null;
      }
      const key = String(l.key || "").trim().toUpperCase();
      if (!key) return null;
      return {
        key,
        url: l.url || `#`,
        summary: l.summary || "",
        status: l.status || "",
        transitions: l.transitions || [],
      };
    })
    .filter(Boolean);
}

export default {
  name: "JiraLinksPanel",
  components: { JiraStatusSelect },
  props: {
    links: { type: Array, default: () => [] },
    editable: { type: Boolean, default: true },
    embedded: { type: Boolean, default: false },
    dense: { type: Boolean, default: false },
  },
  computed: {
    rootClass() {
      return {
        "jira-links-embedded": this.embedded,
        "modal-pane-jira": !this.embedded,
        "jira-panel--dense": this.dense,
      };
    },
  },
  data() {
    return {
      localLinks: [],
      loading: false,
      loadError: null,
    };
  },
  watch: {
    links: {
      deep: true,
      immediate: true,
      handler() {
        this.syncFromProps();
      },
    },
  },
  methods: {
    async syncFromProps() {
      const base = normalizeIncoming(this.links);
      if (base.length === 0) {
        this.localLinks = [];
        return;
      }
      this.loading = true;
      this.loadError = null;
      try {
        const data = await fetchJiraIssuesByKeys(base.map((l) => l.key));
        this.localLinks = enrichJiraLinkRows(base, data);
      } catch (e) {
        this.localLinks = base;
        this.loadError = e.message || "Cannot load Jira status";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-pane-jira {
  width: 380px;
  flex-shrink: 0;
  padding: 1rem;
  overflow: hidden;
  max-height: 65vh;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid #e9ecef;
}
.jira-panel-table-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.jira-panel-table th {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: #8392ab;
  padding: 0.35rem 0.4rem;
  border-bottom: 1px solid #e9ecef;
}
.jira-panel-table td {
  padding: 0.4rem;
  vertical-align: middle;
  border-color: #f0f2f5;
  font-size: 0.75rem;
}
.jira-panel-id { width: 28%; white-space: nowrap; }
.jira-panel-title {
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.jira-panel-status { width: 38%; }
.jira-panel--dense .jira-panel-table {
  table-layout: fixed;
  width: 100%;
}
.jira-panel--dense .jira-panel-id { width: 17%; }
.jira-panel--dense .jira-panel-title {
  max-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.jira-panel--dense .jira-panel-status {
  width: 6.75rem;
  white-space: nowrap;
}
.jira-panel--dense .jira-panel-table th,
.jira-panel--dense .jira-panel-table td {
  font-size: 0.7rem;
  padding: 0.3rem 0.35rem;
}
.jira-status-readonly {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  background: #e9ecef;
  border-radius: 4px;
  color: #495057;
}
.jira-links-embedded {
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
</style>
